import { useState } from "react";
import {
  Box, Typography, Paper, Stack, Button, Alert, Chip, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell, TableHead, TableRow,
} from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { diagnosticsApi, type DxOrderListDto } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { formatDateTime } from "@/shared/datetime";

const STATUSES = ["", "Ordered", "PartiallyResulted", "Resulted", "Cancelled"];

export function DiagnosticOrdersListPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [detailId, setDetailId] = useState<string | null>(null);
  const query = useQuery({ queryKey: ["dx-orders", status], queryFn: () => diagnosticsApi.listOrders(status || undefined) });

  const columns: GridColDef<DxOrderListDto>[] = [
    { field: "accessionNumber", headerName: "Accession", width: 180 },
    { field: "orderType", headerName: "Type", width: 110 },
    { field: "priority", headerName: "Priority", width: 100 },
    { field: "status", headerName: "Status", width: 140, renderCell: p => <StatusPill label={p.row.status} /> },
    { field: "itemCount", headerName: "Items", width: 80, align: "right", headerAlign: "right" },
    { field: "salesInvoiceId", headerName: "Billed", width: 90, renderCell: p => p.row.salesInvoiceId ? <Chip size="small" color="success" label="Yes" /> : <Chip size="small" label="No" variant="outlined" /> },
    { field: "createdAt", headerName: "Created", width: 180, valueGetter: v => v ? formatDateTime(v as string) : "" },
    {
      field: "actions", headerName: "", flex: 1, minWidth: 200, sortable: false, align: "right",
      renderCell: p => (
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => setDetailId(p.row.id)}>View</Button>
          <Button size="small" variant="outlined" onClick={() => navigate(`/diagnostics/collect?orderId=${p.row.id}`)}>Collect</Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Diagnostics"]}
        title="Orders"
        actions={
          <Button variant="contained" onClick={() => navigate("/diagnostics/orders/new")}>New order</Button>
        }
      />
      <Paper sx={{ p: 2 }}>
        <TextField select label="Status" size="small" value={status} onChange={e => setStatus(e.target.value)} sx={{ width: 200, mb: 2 }}>
          {STATUSES.map(s => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
        </TextField>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 560 }}>
          <DataGrid rows={query.data ?? []} columns={columns} loading={query.isLoading} getRowId={r => r.id}
            disableRowSelectionOnClick pageSizeOptions={[25, 50]} initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} />
        </Box>
      </Paper>
      <OrderDetailDialog id={detailId} onClose={() => setDetailId(null)} />
    </Box>
  );
}

function OrderDetailDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const query = useQuery({ queryKey: ["dx-order", id], queryFn: () => diagnosticsApi.getOrder(id!), enabled: !!id });
  const o = query.data;
  return (
    <Dialog open={!!id} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Order {o?.accessionNumber ?? ""}</DialogTitle>
      <DialogContent dividers>
        {query.isError && <Alert severity="error">{errorMessage(query.error)}</Alert>}
        {o && (
          <>
            <Typography variant="body2" gutterBottom>{o.orderType} · {o.priority} · {o.status} · {o.salesInvoiceId ? "Billed" : "Not billed"}</Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
              <Chip size="small" color="info" variant="outlined" label={`Facility: ${o.routedFacilityName ?? "unrouted"}`} />
              <Chip size="small" color="info" variant="outlined" label={`ETA: ${o.estimatedCompletionMinutes} min`} />
            </Stack>
            <Table size="small">
              <TableHead><TableRow><TableCell>Kind</TableCell><TableCell>Test/Procedure</TableCell></TableRow></TableHead>
              <TableBody>{o.items.map(i => <TableRow key={i.id}><TableCell>{i.kind}</TableCell><TableCell>{i.displayName}</TableCell></TableRow>)}</TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
